import { ClientLoggingService } from '../../shared/client-logging.service';
import { Component, OnInit, OnDestroy, ViewChild, ElementRef, inject, NgZone, HostListener } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialogModule } from '@angular/material/dialog';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { map } from 'rxjs/operators';
import { ChatService } from '../../services/chat.service';
import { ChatStateService } from '../../services/chat-state.service';
import { ChatContextSyncService } from '../../services/chat-context-sync.service';
import {
  ChatAttachment,
  ChatMessage,
  ChatContext,
  PinnedPage,
  SendMessageRequest,
  ChatMessageResponse,
  VoiceResponse,
  VoiceHistoryEntry,
  TimelineEvent,
  TOOL_CALL_STATUS,
  ToolCall,
  TokenBreakdown,
  TokenTypeMeta,
  VoiceTranscriptCorrectedEvent,
  VoiceTranscriptDecoratedEvent,
  DEFAULT_CHAT_MODEL_ID,
  DEFAULT_THINKING_LEVEL,
} from '../../models/chat.models';
import { ChatMessageComponent } from './chat-message/chat-message.component';
import { ChatInputComponent, ComposedMessage } from './chat-input/chat-input.component';
import { ChatHistoryListComponent } from './chat-history-list/chat-history-list.component';
import {
  FeedbackDialogComponent,
  FeedbackDialogData,
  FeedbackDialogResult,
} from '../feedback-dialog/feedback-dialog.component';
import { FeedbackService, SubmitFeedbackRequest, SubmitFeedbackResponse } from '../../services/feedback.service';
import { FeedbackReporterService } from '../../services/feedback-reporter.service';
import { FirebaseAuthService } from '../../shared/firebase-auth.service';
import { filter, take, switchMap, finalize } from 'rxjs';

interface RetryableChatTurn {
  request: SendMessageRequest;
  attachments: ChatAttachment[];
  userMessageId: string;
}

/**
 * Main chat component container.
 *
 * Features:
 * - Displays chat panel with messages
 * - Shows context chips (project, file, page, pinned pages)
 * - Handles message sending and streaming
 * - Manages session lifecycle
 */
@Component({
  selector: 'app-chat',
  imports: [
    CommonModule,
    FormsModule,
    MatSidenavModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatExpansionModule,
    MatTooltipModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatProgressBarModule,
    MatDialogModule,
    ChatMessageComponent,
    ChatInputComponent,
    ChatHistoryListComponent,
  ],
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
})
export class ChatComponent implements OnInit, OnDestroy {
  private static readonly MODALITY_VOICE = 'voice';
  private static readonly INTERRUPTED_RESPONSE_FALLBACK =
    '*The response was interrupted. Please reload the page to see any completed response, or try again.*';
  private static readonly FAILED_RESPONSE_MESSAGE = "We couldn't complete that response. Please try again.";
  private static readonly ACTIONABLE_ERROR_PREFIX = 'USER_ACTION_REQUIRED: ';

  // Issue #966: Fallback when the model returns an empty response.
  private static readonly EMPTY_RESPONSE_FALLBACK =
    '*The model returned an empty response. Please try rephrasing your question or using a different model.*';

  /** Duration of the minimize-to-history CSS animation (ms). Keep in sync with chat.component.scss. */
  private static readonly MINIMIZE_ANIMATION_MS = 200;
  /** Duration of the maximize-from-history CSS animation (ms). Keep in sync with chat.component.scss. */
  private static readonly MAXIMIZE_ANIMATION_MS = 200;
  /** Duration of the history button pulse CSS animation (ms). Keep in sync with chat.component.scss. */
  private static readonly HISTORY_PULSE_MS = 800;

  private chatService = inject(ChatService);
  private chatState = inject(ChatStateService);
  private chatContextSync = inject(ChatContextSyncService);
  private feedbackService = inject(FeedbackService);
  private feedbackReporter = inject(FeedbackReporterService);
  private authService = inject(FirebaseAuthService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private doc = inject(DOCUMENT);
  private ngZone = inject(NgZone);

  @ViewChild('messageContainer') private messageContainer!: ElementRef;
  @ViewChild('historyTrigger') historyTrigger!: MatMenuTrigger;
  @ViewChild('historyList') historyList!: ChatHistoryListComponent;
  @ViewChild(ChatInputComponent) private chatInput!: ChatInputComponent;

  private destroy$ = new Subject<void>();
  private isNewSession = false; // Track if first message in new session (Issue #371)
  /** One browser-local unsent draft, isolated from every persisted session. */
  private localDraftMessage = '';
  /** Outstanding lazy session creation for an unsent local draft. */
  private pendingLazyCreateSubscription: Subscription | null = null;
  /** Identity token that prevents a cancelled lazy create callback from sending its stale draft. */
  private lazyCreateToken: object | null = null;
  /** Outstanding persisted-history request, cancelled when another navigation wins. */
  private pendingHistoryLoadSubscription: Subscription | null = null;
  /** Identity token that prevents stale history responses from replacing the active draft. */
  private historyLoadToken: object | null = null;
  /** True after choosing persisted history, including while its request is pending or failed. */
  private viewingPersistedSession = false;
  private activeStreamSubscription: Subscription | null = null;
  activeAssistantMessageId: string | null = null;

  /** Immutable wire request for the active turn, retained for an exact retry. */
  private activeTurn: RetryableChatTurn | null = null;
  private readonly retryableTurns = new Map<string, RetryableChatTurn>();
  private interruptedAssistantMessageId: string | null = null;

  userScrolledUp = false;
  isMinimizing = false;
  isMaximizing = false;
  isHistoryHighlighted = false;
  private readonly SCROLL_THRESHOLD = 60;
  private scrollRafId: number | null = null;
  private animationTimerIds: ReturnType<typeof setTimeout>[] = [];
  /** Dedicated timer handle for history button pulse — cleared on rapid re-selection. */
  private historyPulseTimerId: ReturnType<typeof setTimeout> | null = null;
  /** Dedicated timer handle for maximize animation — cleared on rapid re-selection. */
  private maximizeTimerId: ReturnType<typeof setTimeout> | null = null;
  /** True when loadSession was triggered from user selecting a history item (not initial load). */
  private pendingMaximize = false;

  state$ = this.chatState.state;
  effectiveCost$ = this.chatState.state.pipe(
    map(s => {
      const base = s.sessionCostAnalysis;
      const pending = s.pendingTurnCost;
      if (!pending) return base;
      return {
        totalTokens: (base.totalTokens ?? 0) + (pending.totalTokens ?? 0),
        estimatedTotalCostUsd: (base.estimatedTotalCostUsd ?? 0) + (pending.estimatedTotalCostUsd ?? 0),
        tokenBreakdown: this.mergeBreakdownForDisplay(base.tokenBreakdown, pending.tokenBreakdown),
        contextWindowSize: pending.contextWindowSize ?? base.contextWindowSize,
        model: pending.model ?? base.model,
      };
    })
  );
  chatWidth: number = 400; // Default width
  selectedModel: string = DEFAULT_CHAT_MODEL_ID;
  selectedThinkingLevel: string = DEFAULT_THINKING_LEVEL;
  customBudget: number | null = null;

  isExportingPdf: boolean = false;

  showLlmDetails = false;
  isSnappedToScreen = false;
  private isResizing = false;
  private readonly MIN_WIDTH = 320;
  private readonly MAX_WIDTH = 800;
  /** If chatWidth >= this fraction of viewport, snap to full screen. */
  private static readonly SNAP_THRESHOLD = 0.9;
  private static readonly LS_KEY_WIDTH = 'chat_panel_width';
  private static readonly LS_KEY_MODEL = 'chat_selected_model';
  private static readonly LS_KEY_THINKING = 'chat_thinking_level';
  private static readonly LS_KEY_BUDGET = 'chat_custom_budget';
  private static readonly LS_KEY_LLM_DETAILS = 'chat_show_llm_details';

  // Separate conversation messages from queued messages
  getConversationMessages() {
    return this.state$.pipe(map(state => state.messages.filter(m => !m.queued)));
  }

  getQueuedMessages() {
    return this.state$.pipe(map(state => state.messages.filter(m => m.queued)));
  }

  constructor() {
    const savedWidth = localStorage.getItem(ChatComponent.LS_KEY_WIDTH);
    if (savedWidth) {
      this.chatWidth = parseInt(savedWidth, 10);
    }

    const savedModel = localStorage.getItem(ChatComponent.LS_KEY_MODEL);
    if (savedModel) {
      this.selectedModel = savedModel;
    }

    const savedThinking = localStorage.getItem(ChatComponent.LS_KEY_THINKING);
    if (savedThinking) {
      this.selectedThinkingLevel = savedThinking;
    }

    const savedBudget = localStorage.getItem(ChatComponent.LS_KEY_BUDGET);
    if (savedBudget) {
      this.customBudget = parseInt(savedBudget, 10);
    }

    try {
      this.showLlmDetails = localStorage.getItem(ChatComponent.LS_KEY_LLM_DETAILS) === 'true';
    } catch {
      // localStorage unavailable (SSR/privacy) — keep default (false)
    }
  }

  ngOnInit() {
    // Log initialization for debugging
    ClientLoggingService.log('INFO', '[Chat] Initializing chat component');
    ClientLoggingService.log('INFO', '[Chat] API Base:', this.chatService['API_BASE']);

    // Issue #1048: Check snap-to-screen on startup
    this.checkSnapToScreen();

    // Initialize with existing session or create new one
    this.initializeSession();

    // Load pinned pages from localStorage
    const pinnedPages = this.chatState.loadPinnedPages();
    if (pinnedPages.length > 0) {
      this.chatState.updateContext({ pinnedPages });
    }

    // Listen for UI navigation requests from agent tools (Issue #292)
    this.chatService.navigationRequested$.pipe(takeUntil(this.destroy$)).subscribe(url => {
      ClientLoggingService.log('INFO', '[Chat] Navigation requested by agent tool:', url);
      this.router.navigateByUrl(url);
    });

    // Issue #933: Auto-retry interrupted messages after WebSocket reconnect
    this.chatService.reconnectedWithPendingRetry$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.retryInterruptedMessage();
    });

    // Issue #1004: Handle async decoration updates (MESSAGE_UPDATE chunks)
    this.chatService.messageUpdate$.pipe(takeUntil(this.destroy$)).subscribe(chunk => {
      if (chunk.messageId && chunk.content) {
        this.chatState.updateLastMessage(chunk.content, chunk.messageId);
      }
    });

    this.setupIdleTracking();

    // Issue #740: Update body margin when docked state or open state changes
    this.chatState.state.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.updateDockedLayout();
    });
  }

  // --- Issue #1048: Snap-to-screen on small viewports ---

  @HostListener('window:resize')
  onWindowResize() {
    this.checkSnapToScreen();
  }

  /** The effective width used in the template — full viewport when snapped. */
  get effectiveChatWidth(): number {
    return this.isSnappedToScreen ? window.innerWidth : this.chatWidth;
  }

  /**
   * If chatWidth >= 90% of viewport, snap to full screen and auto-dock.
   * Called on init, window resize, and after manual panel resize.
   */
  private checkSnapToScreen(): void {
    const vw = window.innerWidth;
    if (this.chatWidth >= ChatComponent.SNAP_THRESHOLD * vw) {
      this.isSnappedToScreen = true;
      this.chatState.setDocked(true);
    } else {
      if (this.isSnappedToScreen) {
        // Exiting snap mode — also undock since docking was auto-applied
        this.chatState.setDocked(false);
      }
      this.isSnappedToScreen = false;
    }
    this.updateDockedLayout();
  }

  ngOnDestroy() {
    this.cancelPendingLazyCreate();
    this.cancelPendingHistoryLoad();
    this.clearDockedLayout();
    this.teardownIdleTracking();
    this.chatService.disconnect();
    this.removeFeedbackBeforeUnloadGuard();
    this.animationTimerIds.forEach(id => clearTimeout(id));
    this.animationTimerIds = [];
    if (this.historyPulseTimerId) clearTimeout(this.historyPulseTimerId);
    if (this.maximizeTimerId) clearTimeout(this.maximizeTimerId);
    this.destroy$.next();
    this.destroy$.complete();
    this.stopResize();
  }

  /**
   * Start resizing the chat panel
   */
  startResize(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;

    const onMouseMove = (e: MouseEvent) => {
      if (!this.isResizing) return;

      const newWidth = window.innerWidth - e.clientX;
      this.chatWidth = Math.max(this.MIN_WIDTH, Math.min(this.MAX_WIDTH, newWidth));

      // Issue #1048: Exit snap mode when user manually drags narrower
      if (this.chatWidth < ChatComponent.SNAP_THRESHOLD * window.innerWidth) {
        this.isSnappedToScreen = false;
      }
      this.updateDockedLayout();
    };

    const onMouseUp = () => {
      this.stopResize();
      // Save width to localStorage
      localStorage.setItem(ChatComponent.LS_KEY_WIDTH, this.chatWidth.toString());
      // Re-evaluate snap after resize completes
      this.checkSnapToScreen();
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    // Store cleanup functions
    (this as any)._cleanupResize = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }

  private stopResize() {
    this.isResizing = false;
    if ((this as any)._cleanupResize) {
      (this as any)._cleanupResize();
      delete (this as any)._cleanupResize;
    }
  }

  /**
   * Initialize session - try to load most recent or create new
   */
  private initializeSession() {
    this.chatService
      .listSessions(undefined, 1)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.sessions && response.sessions.length > 0) {
            this.loadSession(response.sessions[0].sessionId);
          } else {
            // No recent sessions, start in draft mode using local createNewSession
            this.createNewSession();
          }
        },
        error: error => {
          ClientLoggingService.log('ERROR', 'Failed to load sessions', error);
          // Fallback to draft mode
          this.createNewSession();
        },
      });
  }

  /**
   * Load existing session with history
   */
  private loadSession(sessionId: string) {
    this.cancelPendingHistoryLoad();
    const loadToken = {};
    this.historyLoadToken = loadToken;
    this.clearRetryState();
    this.chatState.setLoading(true);
    this.chatState.clearContextFill();

    // Pre-connect WebSocket in parallel with history load (Issue #714: reduce TTFT)
    this.chatService.preConnect(sessionId).catch(err => {
      ClientLoggingService.log('WARN', '[Chat] WebSocket pre-connect failed (will retry on send):', err);
    });

    // If restoring from history selection, consume the flag now (loadSession is sync from onSessionSelected)
    const shouldMaximize = this.pendingMaximize;
    if (shouldMaximize) {
      this.pendingMaximize = false;
    }

    const historyLoadSubscription = this.chatService
      .getSessionHistory(sessionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (this.historyLoadToken !== loadToken) return;
          this.historyLoadToken = null;
          this.pendingHistoryLoadSubscription = null;
          if (response.session) {
            this.chatState.setSession(response.session);
          }
          const messages = response.messages || [];
          this.chatState.setMessages(messages);
          this.chatState.recalculateSessionCostAnalysis(messages);
          this.chatState.setLoading(false);
          this.forceScrollToBottom();

          // Start maximize animation AFTER new messages are rendered
          if (shouldMaximize && messages.length > 0) {
            this.isMaximizing = true;
            // Cancel any previous maximize timer from rapid re-selection
            if (this.maximizeTimerId) clearTimeout(this.maximizeTimerId);
            this.maximizeTimerId = setTimeout(() => {
              this.isMaximizing = false;
              this.maximizeTimerId = null;
            }, ChatComponent.MAXIMIZE_ANIMATION_MS);
          }
        },
        error: error => {
          if (this.historyLoadToken !== loadToken) return;
          this.historyLoadToken = null;
          this.pendingHistoryLoadSubscription = null;
          ClientLoggingService.log('ERROR', 'Failed to load session', error);
          this.chatState.setError('Failed to load chat history');
          this.chatState.setLoading(false);
          this.isMaximizing = false;
        },
      });
    if (this.historyLoadToken === loadToken && !historyLoadSubscription.closed) {
      this.pendingHistoryLoadSubscription = historyLoadSubscription;
    }
  }

  /**
   * Create new chat session
   */
  /**
   * Start new chat (local draft only)
   */
  private createNewSession() {
    // Guard against double-click while animation is already in progress
    if (this.isMinimizing) {
      return;
    }

    // Check if current session has messages (Issue #371)
    const currentMessages = this.chatState.currentState.messages;
    const hadMessages = currentMessages && currentMessages.length > 0;
    const restoreLocalDraft = !!this.chatState.currentState.currentSession || this.viewingPersistedSession;

    // New Chat wins over every unresolved navigation or lazy-create callback.
    this.cancelPendingLazyCreate();
    this.cancelPendingHistoryLoad();
    this.viewingPersistedSession = false;

    if (!restoreLocalDraft) {
      this.localDraftMessage = '';
      if (this.chatInput) this.chatInput.message = '';
    }

    // Stop any active stream immediately so stale chunks don't mutate state during/after animation
    if (this.activeStreamSubscription) {
      this.activeStreamSubscription.unsubscribe();
      this.clearActiveStream();
      this.chatState.clearQueue();
      this.chatState.setLoading(false);
    }

    if (hadMessages) {
      // Play minimize-to-history animation before clearing
      this.isMinimizing = true;

      const animTimerId = setTimeout(() => {
        // Animation complete — now reset session data
        this.isMinimizing = false;
        this.resetAndDisconnectSession(restoreLocalDraft);

        // Briefly highlight the history button to draw attention
        this.isHistoryHighlighted = true;
        const pulseTimerId = setTimeout(() => {
          this.isHistoryHighlighted = false;
        }, ChatComponent.HISTORY_PULSE_MS);
        this.animationTimerIds.push(pulseTimerId);
      }, ChatComponent.MINIMIZE_ANIMATION_MS);
      this.animationTimerIds.push(animTimerId);
    } else {
      this.resetAndDisconnectSession(restoreLocalDraft);
    }
  }

  /** Disconnect WebSocket and reset session data (tokens, messages, fill). */
  private resetAndDisconnectSession(restoreLocalDraft = false) {
    this.chatService.disconnect();
    this.clearRetryState();
    this.chatState.resetSessionData();
    if (this.chatInput) this.chatInput.message = restoreLocalDraft ? (this.localDraftMessage ?? '') : '';
    ClientLoggingService.log('INFO', '[Chat] Started new draft session');
  }

  private cancelPendingLazyCreate(): void {
    this.lazyCreateToken = null;
    this.pendingLazyCreateSubscription?.unsubscribe();
    this.pendingLazyCreateSubscription = null;
  }

  private cancelPendingHistoryLoad(): void {
    this.historyLoadToken = null;
    this.pendingHistoryLoadSubscription?.unsubscribe();
    this.pendingHistoryLoadSubscription = null;
  }

  onModelChanged(modelId: string) {
    this.selectedModel = modelId;
    localStorage.setItem(ChatComponent.LS_KEY_MODEL, modelId);
  }

  onThinkingLevelChanged(level: string) {
    this.selectedThinkingLevel = level;
    localStorage.setItem(ChatComponent.LS_KEY_THINKING, level);
    if (level === 'CUSTOM' && this.customBudget == null) {
      this.customBudget = 8192;
      localStorage.setItem(ChatComponent.LS_KEY_BUDGET, '8192');
    } else if (level !== 'CUSTOM') {
      this.customBudget = null;
      localStorage.removeItem(ChatComponent.LS_KEY_BUDGET);
    }
  }

  onCustomBudgetChanged(budget: number | null) {
    this.customBudget = budget;
    if (budget != null) {
      localStorage.setItem(ChatComponent.LS_KEY_BUDGET, budget.toString());
    } else {
      localStorage.removeItem(ChatComponent.LS_KEY_BUDGET);
    }
  }

  toggleLlmDetails() {
    this.showLlmDetails = !this.showLlmDetails;
    try {
      localStorage.setItem(ChatComponent.LS_KEY_LLM_DETAILS, String(this.showLlmDetails));
    } catch {
      ClientLoggingService.log('WARN', '[Chat] localStorage not available, cannot persist LLM details visibility.');
    }
  }

  /**
   * Send user message (or queue if currently processing)
   */
  /**
   * Sends one of the starter suggestions shown on an empty conversation.
   *
   * These are plain text with no attachments, so they get their own entry point rather than
   * constructing an empty attachment list at each call site in the template.
   */
  sendSuggestion(text: string) {
    this.sendMessage({ text, attachments: [] });
  }

  sendMessage(composed: ComposedMessage) {
    const session = this.chatState.currentState.currentSession;
    const message = composed.text;
    const attachments = composed.attachments;
    if (!session) this.localDraftMessage = message;

    // If currently processing, queue the message and show in UI
    if (this.chatState.currentState.isLoading) {
      ClientLoggingService.log('INFO', '[Chat] Currently processing, queueing message');

      // Add queued message to UI (with visual indicator). The attachments travel with it, so the
      // queued bubble shows the same thumbnails the sent message will.
      const queuedId = `queued-${Date.now()}`;
      const queuedMessage: ChatMessage = {
        messageId: queuedId,
        role: 'USER',
        content: message,
        timestamp: new Date(),
        queued: true,
        attachments,
      };
      this.chatState.addMessage(queuedMessage);
      this.chatState.queueMessage({ text: message, attachments });
      this.userScrolledUp = false;
      return;
    }

    // Lazy load: If no session exists, create one now, then send
    if (!session) {
      ClientLoggingService.log('INFO', '[Chat] No active session, creating (lazy load)...');
      const currentContext = this.chatState.currentState.context;
      this.cancelPendingLazyCreate();
      const createToken = {};
      this.lazyCreateToken = createToken;

      const lazyCreateSubscription = this.chatService
        .createSession({
          projectId: currentContext.projectId,
          modelName: this.selectedModel,
        })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: response => {
            if (this.lazyCreateToken !== createToken) return;
            this.lazyCreateToken = null;
            this.pendingLazyCreateSubscription = null;
            this.localDraftMessage = '';
            ClientLoggingService.log('INFO', '[Chat] Created lazy session:', response.sessionId);
            this.chatState.setSession({
              sessionId: response.sessionId,
              userId: '',
              createdAt: new Date(response.createdAt),
              lastMessageAt: new Date(response.createdAt),
              messageCount: 0,
              agentName: 'permitproof_assistant',
              modelName: this.selectedModel,
              status: 'ACTIVE',
            });

            // Now send the message (mark as new session for refresh)
            this.isNewSession = true;
            this.sendMessageNow(message, attachments);
          },
          error: error => {
            if (this.lazyCreateToken !== createToken) return;
            this.lazyCreateToken = null;
            this.pendingLazyCreateSubscription = null;
            if (this.chatInput && !this.chatInput.message) this.chatInput.message = this.localDraftMessage;
            ClientLoggingService.log('ERROR', '[Chat] Failed to create lazy session:', error);
            this.chatState.setError('Failed to start chat session');
          },
        });
      if (this.lazyCreateToken === createToken && !lazyCreateSubscription.closed) {
        this.pendingLazyCreateSubscription = lazyCreateSubscription;
      }
      return;
    }

    // Send immediately
    this.sendMessageNow(message, attachments);
  }

  /**
   * Actually send a message (not queued)
   */
  private sendMessageNow(message: string, attachments: ChatAttachment[] = []) {
    const session = this.chatState.currentState.currentSession;
    if (!session) return;
    const context = this.chatState.currentState.context;
    ClientLoggingService.log('INFO', '[Chat] Sending message with context:', context);

    const request: SendMessageRequest = {
      message,
      attachmentIds: attachments.filter(a => a.state === 'ready').map(a => a.attachmentId),
      context: context ? structuredClone(context) : undefined,
      modelName: this.selectedModel,
      thinkingLevel: this.selectedThinkingLevel,
      thinkingBudget: this.selectedThinkingLevel === 'CUSTOM' ? (this.customBudget ?? undefined) : undefined,
    };

    const userMessageId = this.createUserMessage(message, attachments);

    // Issue #1745: Clear staged selected texts so subsequent turns do not repeat the quote
    if (context?.selectedTexts?.length) {
      this.chatState.clearSelectedTexts();
    }

    this.sendRequest(request, attachments, userMessageId);
  }

  /** Sends a frozen request snapshot, optionally reusing its existing user bubble on retry. */
  private sendRequest(request: SendMessageRequest, attachments: ChatAttachment[], userMessageId: string): void {
    const session = this.chatState.currentState.currentSession;
    if (!session) return;

    const assistantMessageId = this.createAssistantPlaceholder(userMessageId);
    this.activeAssistantMessageId = assistantMessageId;
    this.activeTurn = { request, attachments, userMessageId };
    this.interruptedAssistantMessageId = null;
    this.chatState.setLoading(true);
    this.userScrolledUp = false;
    this.scrollToBottom();
    ClientLoggingService.log('INFO', '[Chat] Created assistant placeholder:', assistantMessageId);

    const requestStartTime = Date.now();
    this.activeStreamSubscription = this.chatService
      .sendMessage(session.sessionId, request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (this.activeAssistantMessageId !== assistantMessageId) {
            ClientLoggingService.log('WARN', '[Chat] Ignoring stale chunk for', assistantMessageId);
            return;
          }
          ClientLoggingService.log('INFO', '[Chat] Received chunk:', response);

          // Issue #2989: a progress note stands in for activity only while nothing else arrives;
          // any real chunk replaces it.
          if (response.type === 'PROGRESS') {
            if (response.content) {
              this.chatState.setMessageProgress(assistantMessageId, response.content);
            }
            return;
          }
          this.chatState.setMessageProgress(assistantMessageId, undefined);

          // Route to appropriate handler based on chunk type
          if (response.type === 'ERROR') {
            this.handleErrorChunk(response, assistantMessageId);
          } else if (response.type === 'THINKING' && response.thinkingContent) {
            this.handleThinkingChunk(response, assistantMessageId, requestStartTime);
          } else if (response.type === 'THINKING_SUMMARY' && response.summary) {
            this.handleThinkingSummary(response, assistantMessageId);
          } else if (response.type === 'TOOL_CALL_START' && response.toolCall) {
            this.handleToolCallStart(response, assistantMessageId);
          } else if (response.type === 'TOOL_CALL_RESULT' && response.toolCall) {
            this.handleToolCallResult(response, assistantMessageId);
          } else if (response.type === 'COST_UPDATE' && response.costAnalysis) {
            this.chatState.setPendingTurnCost(response.costAnalysis);
          } else if (response.content || response.isFinal) {
            this.handleTextChunk(response, assistantMessageId);
          }

          this.scrollToBottom();
        },
        complete: () => {
          this.clearActiveStream();
          this.handleRequestComplete(assistantMessageId);
        },
        error: error => {
          const failedMessageId = this.activeAssistantMessageId;
          this.clearActiveStream();
          this.handleRequestError(error, failedMessageId);
        },
      });
  }

  /** Creates the user bubble once; retries keep this same logical turn visible. */
  private createUserMessage(message: string, attachments: ChatAttachment[] = []): string {
    const selectedTexts = this.chatState.currentState.context?.selectedTexts;
    const userMessageId = `temp-${Date.now()}`;
    const userMessage: ChatMessage = {
      messageId: userMessageId,
      role: 'USER',
      content: message,
      timestamp: new Date(),
      attachments: attachments.length > 0 ? attachments : undefined,
      selectedTexts: selectedTexts && selectedTexts.length > 0 ? [...selectedTexts] : undefined,
    };
    this.chatState.addMessage(userMessage);
    return userMessageId;
  }

  private createAssistantPlaceholder(userMessageId: string): string {
    const assistantMessageId = `temp-${Date.now() + 1}`;
    const assistantMessage: ChatMessage = {
      messageId: assistantMessageId,
      role: 'ASSISTANT',
      content: '',
      timestamp: new Date(),
      metadata: {},
      streaming: true,
    };
    const messages = this.chatState.currentState.messages;
    const userMessageIndex = messages.findIndex(message => message.messageId === userMessageId);
    if (userMessageIndex >= 0 && userMessageIndex < messages.length - 1) {
      const updatedMessages = [...messages];
      updatedMessages.splice(userMessageIndex + 1, 0, assistantMessage);
      this.chatState.setMessages(updatedMessages);
    } else {
      this.chatState.addMessage(assistantMessage);
    }
    return assistantMessageId;
  }

  /**
   * Handle thinking chunk
   */
  private handleThinkingChunk(response: any, assistantMessageId: string, requestStartTime: number) {
    const targetMessage = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);

    // Create copy of timeline to avoid mutating state directly (though ChatStateService handles immutable update now)
    const timeline = [...(targetMessage?.metadata?.timeline || [])];
    const lastEvent = timeline[timeline.length - 1];

    if (lastEvent && lastEvent.type === 'thinking') {
      // Append to existing event (streaming)
      timeline[timeline.length - 1] = {
        ...lastEvent,
        content: (lastEvent.content || '') + response.thinkingContent,
      };
    } else {
      // Start new thinking block
      // If this is the VERY FIRST event, use requestStartTime to account for buffering latency
      // This fixes "Thought for 0.0s" when thinking arrives as a single buffered chunk
      const timestamp = timeline.length === 0 ? requestStartTime : Date.now();

      timeline.push({
        type: 'thinking',
        content: response.thinkingContent,
        timestamp: timestamp,
      });
    }

    this.chatState.updateLastMessageMetadata({ timeline }, assistantMessageId);
  }

  /**
   * Handle inline thinking summary delivered during streaming.
   * Updates the corresponding timeline event's summary field so the header
   * changes from "Thought for X.Xs" to the LLM-generated summary.
   */
  private handleThinkingSummary(response: any, assistantMessageId: string) {
    const targetMessage = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);
    const timeline = [...(targetMessage?.metadata?.timeline || [])];
    const idx = response.timelineEventIndex;

    if (idx != null && idx >= 0 && idx < timeline.length && timeline[idx].type === 'thinking') {
      timeline[idx] = { ...timeline[idx], summary: response.summary };
      this.chatState.updateLastMessageMetadata({ timeline }, assistantMessageId);
      ClientLoggingService.log('INFO', '[Chat] Updated thinking summary for event', idx, response.summary);
    }
  }

  /**
   * Handle tool call start chunk
   */
  private handleToolCallStart(response: any, assistantMessageId: string) {
    const targetMessage = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);
    const toolCalls = targetMessage?.metadata?.toolCalls || [];
    const timeline = targetMessage?.metadata?.timeline || [];

    const newToolCall = {
      toolName: response.toolCall.toolName,
      operationId: response.toolCall.operationId,
      parameters: response.toolCall.parameters || {},
      durationMs: 0,
    };

    this.closeOpenThinkingEvent(timeline);

    this.chatState.updateLastMessageMetadata(
      {
        toolCalls: [...toolCalls, newToolCall],
        timeline: [
          ...timeline,
          {
            type: 'tool_call',
            toolCall: newToolCall,
            timestamp: Date.now(),
          },
        ],
      },
      assistantMessageId
    );
  }

  /**
   * Handle tool call result chunk
   */
  private handleToolCallResult(response: any, assistantMessageId: string) {
    const targetMessage = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);

    if (targetMessage && targetMessage.metadata?.toolCalls) {
      const toolCalls = [...targetMessage.metadata.toolCalls];
      const timeline = targetMessage.metadata.timeline ? [...targetMessage.metadata.timeline] : [];

      const index = toolCalls.findIndex(tc => tc.operationId === response.toolCall!.operationId);

      if (index >= 0) {
        // Prefer backend-reported duration; fall back to frontend wall-clock measurement
        let durationMs = response.toolCall.durationMs;
        if (!durationMs) {
          const startEvent = timeline.find(
            e => e.type === 'tool_call' && e.toolCall?.operationId === response.toolCall!.operationId
          );
          durationMs = startEvent?.timestamp ? Date.now() - startEvent.timestamp : 0;
        }

        const updatedToolCall = {
          ...toolCalls[index],
          parameters: response.toolCall.parameters || toolCalls[index].parameters,
          result: response.toolCall.result,
          error: response.toolCall.error,
          status: response.toolCall.status,
          durationMs,
        };

        toolCalls[index] = updatedToolCall;

        // Also update in timeline
        const timelineIndex = timeline.findIndex(
          e => e.type === 'tool_call' && e.toolCall?.operationId === response.toolCall!.operationId
        );
        if (timelineIndex >= 0) {
          timeline[timelineIndex] = {
            ...timeline[timelineIndex],
            toolCall: updatedToolCall,
          };
        }

        this.chatState.updateLastMessageMetadata({ toolCalls, timeline }, assistantMessageId);
      }
    }
  }

  /**
   * Handle text chunk
   */
  private handleTextChunk(response: any, assistantMessageId: string) {
    const targetMessage = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);
    if (targetMessage?.metadata?.timeline) {
      const timeline = [...targetMessage.metadata.timeline];
      if (this.closeOpenThinkingEvent(timeline)) {
        this.chatState.updateLastMessageMetadata({ timeline }, assistantMessageId);
      }
    }

    if (response.content) {
      this.chatState.updateLastMessage(response.content, assistantMessageId);
    }

    // Store cost metadata before replacing the temporary message ID. Once the
    // persisted ID is installed, lookups by assistantMessageId no longer find
    // the message and the per-message usage details would be discarded.
    // totalTokens may be omitted by JsonFormat.printer() when 0, so check for
    // the object itself or any non-zero field rather than requiring tokens.
    if (
      response.costAnalysis &&
      ((response.costAnalysis.totalTokens ?? 0) > 0 ||
        response.costAnalysis.tokenBreakdown ||
        response.costAnalysis.model)
    ) {
      this.chatState.updateLastMessageMetadata({ costAnalysis: response.costAnalysis }, assistantMessageId);
      // Only accumulate into session total on isFinal to avoid double-counting
      // (backend sends costAnalysis on both pre-decoration and final chunks).
      if (response.isFinal) {
        this.chatState.addCostAnalysis(response.costAnalysis);
        this.chatState.clearPendingTurnCost();
      }
    }

    if (response.isFinal) {
      // Issue #966: Detect truly empty responses (no text AND no tool calls/thinking)
      // before updateMessageId changes the lookup key.
      if (!response.content && !targetMessage?.metadata?.timeline?.length) {
        ClientLoggingService.log('WARN', '[Chat] Empty model response, setting fallback message');
        this.chatState.updateLastMessage(ChatComponent.EMPTY_RESPONSE_FALLBACK, assistantMessageId);
      }

      if (response.reasoningUnavailable) {
        this.chatState.updateLastMessageMetadata({ reasoningUnavailable: true }, assistantMessageId);
      }

      this.markAsStreaming(assistantMessageId, false);
      if (response.messageId && response.messageId !== assistantMessageId) {
        this.updateMessageId(assistantMessageId, response.messageId);
      }
    }
  }

  private mergeBreakdownForDisplay(
    a: TokenBreakdown | undefined,
    b: TokenBreakdown | undefined
  ): TokenBreakdown | undefined {
    if (!a && !b) return undefined;
    if (!a) return b;
    if (!b) return a;
    const add = (x: TokenTypeMeta | undefined, y: TokenTypeMeta | undefined) => {
      if (!x && !y) return undefined;
      return { tokenCount: (x?.tokenCount ?? 0) + (y?.tokenCount ?? 0) };
    };
    return {
      nonCachedInput: add(a.nonCachedInput, b.nonCachedInput),
      cachedContent: add(a.cachedContent, b.cachedContent),
      output: add(a.output, b.output),
      thinking: add(a.thinking, b.thinking),
      toolUse: add(a.toolUse, b.toolUse),
    };
  }

  private markAsStreaming(messageId: string, streaming: boolean) {
    const messages = [...this.chatState.currentState.messages];
    const index = messages.findIndex(m => m.messageId === messageId);

    if (index >= 0) {
      messages[index] = {
        ...messages[index],
        streaming,
      };
      this.chatState.setMessages(messages);
    }
  }

  private updateMessageId(oldId: string, newId: string) {
    const messages = [...this.chatState.currentState.messages];
    const index = messages.findIndex(m => m.messageId === oldId);
    if (index >= 0) {
      messages[index] = { ...messages[index], messageId: newId };
      this.chatState.setMessages(messages);
    }
  }

  /**
   * Handle request completion
   */
  private handleRequestComplete(assistantMessageId?: string) {
    ClientLoggingService.log('INFO', '[Chat] Request completed, setting loading=false');

    if (assistantMessageId) {
      const targetMessage = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);
      if (targetMessage?.metadata?.timeline) {
        const timeline = [...targetMessage.metadata.timeline];
        if (this.closeOpenThinkingEvent(timeline)) {
          this.chatState.updateLastMessageMetadata({ timeline }, assistantMessageId);
        }
      }

      // Issue #854: Ensure streaming flag is cleared even if no
      // isFinal chunk was received (e.g., WebSocket closed mid-stream).
      if (targetMessage?.streaming) {
        this.markAsStreaming(assistantMessageId, false);
      }

      if (!targetMessage?.failed) {
        this.retryableTurns.delete(assistantMessageId);
        this.activeTurn = null;
        this.interruptedAssistantMessageId = null;
      }
    }

    // Refresh history list if this was first message in a new session (Issue #371)
    if (this.isNewSession && this.historyList) {
      ClientLoggingService.log('INFO', '[Chat] New session first message complete, refreshing history list');
      this.historyList.loadSessions();
      this.isNewSession = false;
    }

    // Give a moment for final chunks to be processed
    setTimeout(() => {
      this.chatState.setLoading(false);

      // Process queued messages after state settles
      setTimeout(() => this.processQueue(), 300);
    }, 100);
  }

  /**
   * Handle an ERROR-type chunk from the backend (e.g., model unavailable, rate limit).
   * Shows the error inline in the assistant message bubble only (no separate banner).
   */
  private handleErrorChunk(response: ChatMessageResponse, assistantMessageId: string) {
    // The backend also sanitizes this boundary. Keep a fixed client-side message as defence in
    // depth so a mixed-version server can never render raw SDK exception text in the chat.
    ClientLoggingService.log('ERROR', '[Chat] Backend reported a failed response');
    const backendContent = typeof response.content === 'string' ? response.content : '';
    const isTrustedActionableError = backendContent.startsWith(ChatComponent.ACTIONABLE_ERROR_PREFIX);
    const errorMessage = isTrustedActionableError
      ? backendContent.slice(ChatComponent.ACTIONABLE_ERROR_PREFIX.length)
      : ChatComponent.FAILED_RESPONSE_MESSAGE;
    const canRetry = (response.retryable ?? !isTrustedActionableError) && this.activeTurn !== null;

    this.markInProgressToolCallsFailed(assistantMessageId, errorMessage);
    const messages = [...this.chatState.currentState.messages];
    const index = messages.findIndex(message => message.messageId === assistantMessageId);
    if (index >= 0) {
      messages[index] = {
        ...messages[index],
        content: errorMessage,
        streaming: false,
        failed: true,
        retryable: canRetry,
        errorCode: response.errorCode,
        errorCorrelationId: response.errorCorrelationId,
        errorAction: response.errorAction,
      };
      this.chatState.setMessages(messages);
    }
    if (canRetry && this.activeTurn) {
      this.retryableTurns.set(assistantMessageId, this.activeTurn);
    } else {
      this.retryableTurns.delete(assistantMessageId);
      this.activeTurn = null;
    }
    this.chatState.clearPendingTurnCost();
    this.chatState.setLoading(false);
  }

  /**
   * Handle request error.
   * Marks any in-progress tool calls on the active message as failed so the
   * error is collocated with the timeline event rather than only shown as a
   * generic banner at the bottom of the chat panel (Issue #803).
   */
  private handleRequestError(error: any, assistantMessageId: string | null = null) {
    const errorMsg = error?.message || error?.toString?.() || 'Connection lost';
    ClientLoggingService.log('ERROR', '[Chat] Stream error:', errorMsg, error);

    // Issue #933: If this is a connection-loss error and we have a message to retry,
    // show a "Reconnecting..." state instead of the final error. The auto-reconnect
    // in ChatService will emit reconnectedWithPendingRetry$ on success, which
    // triggers retryInterruptedMessage().
    // Use a regex anchored to the specific WebSocket close codes to avoid false
    // positives (e.g. the fallback 'Connection lost' default when error is null).
    const isConnectionLoss = /Connection lost.*code=(1001|1006)/.test(errorMsg);

    if (isConnectionLoss && this.activeTurn && assistantMessageId) {
      ClientLoggingService.log('INFO', '[Chat] Connection lost mid-stream, waiting for reconnect to auto-retry');
      this.interruptedAssistantMessageId = assistantMessageId;
      this.retryableTurns.set(assistantMessageId, this.activeTurn);

      this.markInProgressToolCallsFailed(assistantMessageId, 'Connection lost — retrying...');
      this.markAsStreaming(assistantMessageId, false);

      const msg = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);
      if (msg && !msg.content) {
        this.chatState.updateLastMessage('*Reconnecting — will retry automatically...*', assistantMessageId);
      }

      // Keep isLoading=true so any messages the user sends while waiting for
      // reconnect are queued instead of overwriting lastSentMessageText.
      return;
    }

    const failedToolCalls = this.markInProgressToolCallsFailed(assistantMessageId, errorMsg);

    if (!failedToolCalls) {
      this.chatState.setError('Failed to send message');
    }

    // Issue #854: Ensure the message exits streaming state so the UI
    // no longer shows "Thinking..." subscript or streaming indicators after an error.
    if (assistantMessageId) {
      this.markAsStreaming(assistantMessageId, false);

      const msg = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);
      if (msg && !msg.content) {
        this.chatState.updateLastMessage(ChatComponent.INTERRUPTED_RESPONSE_FALLBACK, assistantMessageId);
      }
    }

    this.activeTurn = null;
    this.interruptedAssistantMessageId = null;

    this.chatState.setLoading(false);
  }

  /**
   * Issue #933: Retry the last message after a successful WebSocket reconnect.
   * Removes the failed assistant message (with its partial timeline/tool calls)
   * and re-sends the original user message to get a fresh response.
   */
  private retryInterruptedMessage(): void {
    const failedMsgId = this.interruptedAssistantMessageId;
    const turn = failedMsgId ? this.retryableTurns.get(failedMsgId) : undefined;

    if (!turn || !failedMsgId) {
      ClientLoggingService.log('INFO', '[Chat] Reconnected but no pending message to retry');
      return;
    }

    ClientLoggingService.log('INFO', '[Chat] Auto-retrying interrupted message after reconnect');

    // Clear retry state before re-sending
    this.interruptedAssistantMessageId = null;
    this.retryableTurns.delete(failedMsgId);
    this.chatState.setMessages(this.chatState.currentState.messages.filter(m => m.messageId !== failedMsgId));

    // Clear any error banner from the failed attempt
    this.chatState.setError(null);

    // Reuse the same user bubble and exact frozen wire request. This preserves attachments,
    // context, model, and thinking settings even if the live controls changed after the failure.
    this.sendRequest(turn.request, turn.attachments, turn.userMessageId);
  }

  retryFailedMessage(assistantMessageId: string): void {
    if (this.chatState.currentState.isLoading) return;
    const turn = this.retryableTurns.get(assistantMessageId);
    if (!turn) return;

    this.retryableTurns.delete(assistantMessageId);
    this.chatState.setMessages(this.chatState.currentState.messages.filter(m => m.messageId !== assistantMessageId));
    this.chatState.setError(null);
    this.sendRequest(turn.request, turn.attachments, turn.userMessageId);
  }

  private clearRetryState(): void {
    this.activeTurn = null;
    this.interruptedAssistantMessageId = null;
    this.retryableTurns?.clear();
  }

  /**
   * Find in-progress tool calls on the given message and mark them as failed.
   * Also closes any open thinking event. Returns true if at least one tool
   * call was marked.
   */
  private markInProgressToolCallsFailed(messageId: string | null, errorMsg: string): boolean {
    if (!messageId) return false;

    const targetMessage = this.chatState.currentState.messages.find(m => m.messageId === messageId);
    if (!targetMessage?.metadata) return false;

    const toolCalls = targetMessage.metadata.toolCalls ? [...targetMessage.metadata.toolCalls] : [];
    const timeline = targetMessage.metadata.timeline ? [...targetMessage.metadata.timeline] : [];
    let toolCallsFailed = false;

    for (let i = 0; i < toolCalls.length; i++) {
      if (!toolCalls[i].result && !toolCalls[i].error) {
        const startEvent = timeline.find(
          e => e.type === 'tool_call' && e.toolCall?.operationId === toolCalls[i].operationId
        );
        const durationMs = startEvent?.timestamp ? Date.now() - startEvent.timestamp : 0;

        toolCalls[i] = { ...toolCalls[i], error: errorMsg, durationMs };

        const timelineIdx = timeline.findIndex(
          e => e.type === 'tool_call' && e.toolCall?.operationId === toolCalls[i].operationId
        );
        if (timelineIdx >= 0) {
          timeline[timelineIdx] = { ...timeline[timelineIdx], toolCall: toolCalls[i] };
        }
        toolCallsFailed = true;
      }
    }

    const thinkingClosed = this.closeOpenThinkingEvent(timeline);

    if (toolCallsFailed || thinkingClosed) {
      this.chatState.updateLastMessageMetadata({ toolCalls, timeline }, messageId);
    }
    return toolCallsFailed;
  }

  /**
   * Close the last timeline event if it is an open thinking block.
   * Mutates the array in-place — callers must spread-copy before passing.
   * Returns true if the timeline was modified.
   */
  private closeOpenThinkingEvent(timeline: TimelineEvent[]): boolean {
    const lastEvent = timeline[timeline.length - 1];
    if (!lastEvent || lastEvent.type !== 'thinking' || lastEvent.durationMs != null) return false;

    if (!lastEvent.content) {
      timeline.pop();
    } else {
      timeline[timeline.length - 1] = {
        ...lastEvent,
        durationMs: Date.now() - (lastEvent.timestamp || Date.now()),
      };
    }
    return true;
  }

  /**
   * Process queued messages (ensure one at a time)
   */
  private processQueue() {
    // Don't process if still loading (ensures alternation)
    if (this.chatState.currentState.isLoading) {
      ClientLoggingService.log('INFO', '[Chat] Still loading, skipping queue processing');
      return;
    }

    const nextMessage = this.chatState.dequeueMessage();
    if (nextMessage) {
      ClientLoggingService.log('INFO', '[Chat] Processing queued message');

      // Remove exactly ONE queued bubble - the first whose text matches - rather than every bubble
      // carrying that text. Filtering by predicate removed all of them, so queueing the same short
      // message twice, which is ordinary for "OK" or "thanks", erased both bubbles when only one
      // was dequeued. The queue then still held an entry with nothing on screen representing it,
      // and the user watched a message they had sent disappear.
      const messages = [...this.chatState.currentState.messages];
      const queuedIndex = messages.findIndex(m => m.queued && m.content === nextMessage.text);
      if (queuedIndex !== -1) {
        messages.splice(queuedIndex, 1);
      }
      this.chatState.setMessages(messages);

      // Send the message (will add user message + assistant placeholder at end)
      setTimeout(() => this.sendMessageNow(nextMessage.text, nextMessage.attachments), 100);
    }
  }

  /**
   * Stop the active agent response stream.
   * Unsubscribes from the WebSocket observable and finalizes the current message
   * with whatever content has been received so far.
   */
  stopGeneration() {
    if (this.activeStreamSubscription) {
      this.activeStreamSubscription.unsubscribe();
      const messageId = this.activeAssistantMessageId;
      this.clearActiveStream();

      if (messageId) {
        this.markInProgressToolCallsFailed(messageId, 'Generation stopped by user');
        const msg = this.chatState.currentState.messages.find(m => m.messageId === messageId);
        if (msg && !msg.content) {
          this.chatState.updateLastMessage('*Generation stopped by user.*', messageId);
        }
      }

      this.handleRequestComplete(messageId ?? undefined);
      this.chatState.clearQueue();
      this.chatState.setLoading(false);
      ClientLoggingService.log('INFO', '[Chat] Generation stopped by user');
    }
  }

  private clearActiveStream() {
    this.activeStreamSubscription = null;
    this.activeAssistantMessageId = null;
  }

  /**
   * Handle scroll events on the message container.
   * Throttled via requestAnimationFrame to avoid excessive change detection.
   * Only re-enters Angular zone when userScrolledUp actually changes.
   */
  onScroll() {
    if (!this.messageContainer || this.scrollRafId !== null) return;
    this.scrollRafId = requestAnimationFrame(() => {
      this.scrollRafId = null;
      const el = this.messageContainer!.nativeElement;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const shouldBeScrolledUp = distanceFromBottom > this.SCROLL_THRESHOLD;
      if (shouldBeScrolledUp !== this.userScrolledUp) {
        this.userScrolledUp = shouldBeScrolledUp;
      }
    });
  }

  /**
   * Programmatically scroll to bottom and re-snap.
   */
  forceScrollToBottom() {
    this.userScrolledUp = false;
    this.doScrollToBottom();
  }

  /**
   * Scroll messages to bottom (respects userScrolledUp flag).
   */
  private scrollToBottom() {
    if (this.userScrolledUp) return;
    this.doScrollToBottom();
  }

  private doScrollToBottom() {
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        if (this.messageContainer) {
          const element = this.messageContainer.nativeElement;
          element.scrollTop = element.scrollHeight;
        }
      }, 50);
    });
  }

  /**
   * Check if has context
   */
  hasContext(): boolean {
    const ctx = this.chatState.currentState.context;
    return !!(ctx.projectId || ctx.fileId || ctx.pageNumber || (ctx.pinnedPages && ctx.pinnedPages.length > 0));
  }

  /**
   * Handle voice start requested (Issue #802).
   * Ensures a session exists (creating one lazily if needed), then
   * delegates to ChatInputComponent to start the actual voice session.
   * Clears any previous voice error so retries show a clean slate.
   */
  private static readonly MAX_VOICE_HISTORY_ENTRIES = 20;
  private static readonly MAX_VOICE_HISTORY_CONTENT_LENGTH = 500;

  onVoiceStartRequested(): void {
    this.finalizeActiveVoiceAssistant();
    this.activeVoiceMessageId = {};

    if (this.chatState.currentState.error) {
      this.chatState.setError(null);
    }

    const currentContext = this.chatContextSync.syncCurrentRoute();
    const history = this.collectConversationHistory();

    const session = this.chatState.currentState.currentSession;
    if (session) {
      this.chatInput.startVoiceWithSession(session.sessionId, history, currentContext);
      return;
    }

    ClientLoggingService.log('INFO', '[Chat] No session for voice — creating lazily...');
    const voiceModelName = this.chatInput.voiceModel?.id ?? this.selectedModel;

    this.chatService
      .createSession({
        projectId: currentContext.projectId,
        modelName: voiceModelName,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          ClientLoggingService.log('INFO', '[Chat] Created lazy session for voice:', response.sessionId);
          this.chatState.setSession({
            sessionId: response.sessionId,
            userId: '',
            createdAt: new Date(response.createdAt),
            lastMessageAt: new Date(response.createdAt),
            messageCount: 0,
            agentName: 'permitproof_assistant',
            modelName: voiceModelName,
            status: 'ACTIVE',
          });
          this.chatInput.startVoiceWithSession(response.sessionId, history, currentContext);
        },
        error: error => {
          ClientLoggingService.log('ERROR', '[Chat] Failed to create session for voice:', error);
          this.chatState.setError('Failed to start voice session');
        },
      });
  }

  /**
   * Collects the most recent chat messages as conversation history entries
   * for the voice session context primer (Issue #839).
   */
  collectConversationHistory(): VoiceHistoryEntry[] {
    const messages = this.chatState.currentState.messages;
    if (!messages || messages.length === 0) return [];

    const maxLen = ChatComponent.MAX_VOICE_HISTORY_CONTENT_LENGTH;
    return messages
      .filter((m: ChatMessage) => m.role === 'USER' || m.role === 'ASSISTANT')
      .filter((m: ChatMessage) => m.content && m.content.trim().length > 0)
      .slice(-ChatComponent.MAX_VOICE_HISTORY_ENTRIES)
      .map((m: ChatMessage) => ({
        role: m.role as 'USER' | 'ASSISTANT',
        content: m.content.length > maxLen ? m.content.substring(0, maxLen) + '...' : m.content,
      }));
  }

  onVoiceError(errorMessage: string): void {
    this.chatState.setError(errorMessage);
  }

  /** Reset voice message tracking so the next transcript starts a new bubble (Issue #869). */
  onVoiceReconnected(): void {
    const assistantMessageId = this.activeVoiceMessageId.assistant;
    const assistantMessage = this.chatState.currentState.messages.find(m => m.messageId === assistantMessageId);
    const hasPendingToolCall = assistantMessage?.metadata?.toolCalls?.some(tc => !tc.result && !tc.error);
    if (hasPendingToolCall) {
      return;
    }
    this.finalizeActiveVoiceAssistant();
    this.activeVoiceMessageId = {};
  }

  private activeVoiceMessageId: { user?: string; assistant?: string } = {};
  private voiceMessageCounter = 0;
  /** Issue #3356: the model ended its turn, so its next speech or tool call is a new reply. */
  private voiceAssistantTurnEnded = false;

  /**
   * Handle the end of a model turn in live voice chat (Issue #3356).
   *
   * The voice service stores each model turn as its own message, so a reloaded session shows one
   * bubble per turn. The live chat matches that: whatever the model says or calls next starts a new
   * bubble, instead of being added to this one with no space between the replies.
   */
  onVoiceTurnComplete(): void {
    if (this.activeVoiceMessageId.assistant) {
      this.voiceAssistantTurnEnded = true;
    }
  }

  /** Close the assistant bubble of a turn that has ended, so the next reply gets its own. */
  private closeEndedVoiceAssistantTurn(): void {
    if (this.voiceAssistantTurnEnded && this.activeVoiceMessageId.assistant) {
      this.finalizeActiveVoiceAssistant();
      this.activeVoiceMessageId.assistant = undefined;
    }
    this.voiceAssistantTurnEnded = false;
  }

  private finalizeActiveVoiceAssistant(): void {
    const messageId = this.activeVoiceMessageId.assistant;
    if (!messageId) return;
    const message = this.chatState.currentState.messages.find(m => m.messageId === messageId);
    if (message) {
      this.chatState.updateLastMessage(message.content, messageId, false);
    }
  }

  /**
   * Handle voice transcript from live voice chat (Issue #802).
   *
   * Partial transcripts update the current message in-place.
   * When a transcript is finished (partial=false), the active message ID
   * is cleared so the next utterance starts a new chat bubble.
   */
  onVoiceTranscript(event: { role: 'user' | 'assistant'; text: string; partial: boolean }) {
    const chatRole = event.role === 'user' ? 'USER' : 'ASSISTANT';
    const activeKey = event.role;

    if (!event.text.trim().length) return;

    if (activeKey === 'assistant') {
      this.closeEndedVoiceAssistantTurn();
    }
    const existingId = this.activeVoiceMessageId[activeKey];

    if (existingId) {
      const existing = this.chatState.currentState.messages.find(m => m.messageId === existingId);
      const accumulated = (existing?.content ?? '') + event.text;
      if (event.role === 'assistant') {
        this.chatState.updateLastMessage(accumulated, existingId, true);
      } else {
        this.chatState.updateLastMessage(accumulated, existingId);
      }
    } else {
      const msgId = `voice-${++this.voiceMessageCounter}-${activeKey}`;
      const msg: ChatMessage = {
        messageId: msgId,
        role: chatRole,
        content: event.text,
        timestamp: new Date(),
        modality: ChatComponent.MODALITY_VOICE,
        streaming: event.role === 'assistant',
      };
      this.chatState.addMessage(msg);
      this.activeVoiceMessageId[activeKey] = msgId;

      // Issue #1209: When a new speaker starts, finalize the previous speaker's bubble.
      // Issue #3356: this now happens even when a tool call in the assistant's bubble has no result
      // yet. Keeping the bubble open put the assistant's next reply above the user's new words; a
      // late result still reaches its own row, by operationId (onVoiceToolCall).
      const otherKey = activeKey === 'user' ? 'assistant' : 'user';
      const otherMsgId = this.activeVoiceMessageId[otherKey];
      if (otherMsgId) {
        if (otherKey === 'assistant') {
          this.finalizeActiveVoiceAssistant();
          this.voiceAssistantTurnEnded = false;
        }
        this.activeVoiceMessageId[otherKey] = undefined;
      }
    }

    // Issue #1209: For user transcripts, partial=false means the user finished
    // speaking. Finalize the user bubble so the next user utterance starts fresh.
    // Assistant bubbles close when the model's turn ends (onVoiceTurnComplete) or when
    // the user speaks (otherKey clearing above).
    if (!event.partial && activeKey === 'user') {
      this.activeVoiceMessageId[activeKey] = undefined;
    }

    this.scrollToBottom();
  }

  /**
   * Ensures an active assistant voice message exists and returns its ID.
   * If none exists, creates one with empty content.
   */
  private getOrCreateVoiceAssistantMessage(): string {
    this.closeEndedVoiceAssistantTurn();
    const existingId = this.activeVoiceMessageId['assistant'];
    if (existingId) return existingId;

    const msgId = `voice-${++this.voiceMessageCounter}-assistant`;
    const msg: ChatMessage = {
      messageId: msgId,
      role: 'ASSISTANT',
      content: '',
      timestamp: new Date(),
      metadata: { timeline: [], toolCalls: [] },
      modality: ChatComponent.MODALITY_VOICE,
      streaming: true,
    };
    this.chatState.addMessage(msg);
    this.activeVoiceMessageId['assistant'] = msgId;
    this.activeVoiceMessageId['user'] = undefined;
    return msgId;
  }

  onVoiceToolCall(event: VoiceResponse): void {
    if (event.type === 'tool_call_start') {
      const msgId = this.getOrCreateVoiceAssistantMessage();
      const target = this.chatState.currentState.messages.find(m => m.messageId === msgId);
      const toolCalls = target?.metadata?.toolCalls || [];
      const timeline = target?.metadata?.timeline || [];

      let parsedParameters: any = {};
      if (event.parameters) {
        try {
          parsedParameters = JSON.parse(event.parameters);
        } catch {
          parsedParameters = event.parameters;
        }
      }

      const newToolCall = {
        toolName: event.toolName || 'unknown_tool',
        operationId: event.operationId || `op-${Date.now()}`,
        parameters: parsedParameters,
        durationMs: 0,
      };

      const lastEvent = timeline[timeline.length - 1];
      if (lastEvent && lastEvent.type === 'thinking' && !lastEvent.durationMs) {
        if (!lastEvent.content) {
          timeline.pop();
        } else {
          timeline[timeline.length - 1] = {
            ...lastEvent,
            durationMs: Date.now() - (lastEvent.timestamp || Date.now()),
          };
        }
      }

      this.chatState.updateLastMessageMetadata(
        {
          toolCalls: [...toolCalls, newToolCall],
          timeline: [...timeline, { type: 'tool_call', toolCall: newToolCall, timestamp: Date.now() }],
        } as any,
        msgId
      );
      this.scrollToBottom();
    } else if (event.type === 'tool_call_result') {
      const row = this.findVoiceToolCallRow(event);
      if (!row) return;
      const { msgId, toolCalls, timeline, index } = row;
      const toolName = event.toolName || 'unknown_tool';
      const startEvent = timeline.find(
        e => e.type === 'tool_call' && e.toolCall?.operationId === toolCalls[index].operationId
      );
      const durationMs = startEvent?.timestamp ? Date.now() - startEvent.timestamp : 0;

      // Issue #3356: the voice service stopped waiting for this tool and told the model so.
      if (event.status === TOOL_CALL_STATUS.TIMED_OUT) {
        const seconds = event.timeoutSeconds ?? 10;
        this.writeVoiceToolCall(msgId, toolCalls, timeline, index, {
          ...toolCalls[index],
          status: TOOL_CALL_STATUS.TIMED_OUT,
          error: `Timed out: the assistant stopped waiting after ${seconds} s.`,
          durationMs,
        });
        this.scrollToBottom();
        return;
      }

      let parsedResult: any = {};
      let parseError: string | undefined;
      if (event.result) {
        try {
          parsedResult = JSON.parse(event.result);
        } catch {
          parsedResult = { rawResponse: event.result };
          parseError = `Non-JSON tool response: ${event.result}`;
        }
      }

      // Issue #3356: the model was told this call timed out and never received this result.
      // Keep it for inspection, but don't show it as a success or act on it.
      if (event.late) {
        this.writeVoiceToolCall(msgId, toolCalls, timeline, index, {
          ...toolCalls[index],
          result: parsedResult,
          status: TOOL_CALL_STATUS.TIMED_OUT,
          error: toolCalls[index].error ?? 'Timed out: the assistant stopped waiting before this result arrived.',
          finishedAfterTimeout: true,
        });
        this.scrollToBottom();
        return;
      }

      const error = parseError || this.extractToolCallError(parsedResult);
      const grpcCode = this.extractGrpcCode(parsedResult);
      const GRPC_NOT_FOUND = 5;
      let voiceStatus: 'COMPLETED' | 'NOT_FOUND' | 'FAILED';
      if (grpcCode === GRPC_NOT_FOUND) {
        voiceStatus = TOOL_CALL_STATUS.NOT_FOUND;
      } else if (grpcCode > 0 || error) {
        voiceStatus = TOOL_CALL_STATUS.FAILED;
      } else if (this.isNotFoundResult(parsedResult)) {
        voiceStatus = TOOL_CALL_STATUS.NOT_FOUND;
      } else {
        voiceStatus = TOOL_CALL_STATUS.COMPLETED;
      }
      const updatedToolCall = {
        ...toolCalls[index],
        result: parsedResult,
        error,
        status: voiceStatus,
        durationMs,
      };
      this.writeVoiceToolCall(msgId, toolCalls, timeline, index, updatedToolCall);

      this.interceptVoiceUiTool(toolName, updatedToolCall.result);
      this.scrollToBottom();
    }
  }

  /**
   * Find the chat row of a voice tool result (Issue #3356).
   *
   * A result carries the operationId of its call, so it reaches its own row even after the
   * assistant's bubble has closed (the user spoke, or the model started another turn). Results
   * from an older voice service carry no operationId; they match the first unfinished call of the
   * same tool in the open assistant bubble, as before.
   */
  private findVoiceToolCallRow(
    event: VoiceResponse
  ): { msgId: string; toolCalls: ToolCall[]; timeline: TimelineEvent[]; index: number } | undefined {
    const messages = this.chatState.currentState.messages;
    const candidates = event.operationId
      ? [...messages].reverse()
      : messages.filter(m => m.messageId === this.activeVoiceMessageId['assistant']);
    for (const message of candidates) {
      const calls = message.metadata?.toolCalls;
      if (!calls?.length) continue;
      const index = event.operationId
        ? calls.findIndex(tc => tc.operationId === event.operationId)
        : calls.findIndex(tc => tc.toolName === (event.toolName || 'unknown_tool') && !tc.result && !tc.error);
      if (index >= 0) {
        return {
          msgId: message.messageId!,
          toolCalls: [...calls],
          timeline: message.metadata?.timeline ? [...message.metadata.timeline] : [],
          index,
        };
      }
    }
    return undefined;
  }

  /** Store an updated voice tool call in its message's tool calls and timeline. */
  private writeVoiceToolCall(
    msgId: string,
    toolCalls: ToolCall[],
    timeline: TimelineEvent[],
    index: number,
    updated: ToolCall
  ): void {
    toolCalls[index] = updated;
    const timelineIndex = timeline.findIndex(
      e => e.type === 'tool_call' && e.toolCall?.operationId === updated.operationId
    );
    if (timelineIndex >= 0) {
      timeline[timelineIndex] = { ...timeline[timelineIndex], toolCall: updated };
    }
    this.chatState.updateLastMessageMetadata({ toolCalls, timeline } as any, msgId);
  }

  /**
   * Extract error message from a tool call result, mirroring the 4 conventions
   * recognized by the backend (ChatAgentService.extractErrorFromFunctionResponse):
   *   1. { "error": "msg" }
   *   2. { "success": false, "errorMessage": "msg" }
   *   3. { "success": false, "error_message": "msg" }
   *   4. { "status": "error", "error_message": "msg" }
   */
  private extractToolCallError(result: any): string | undefined {
    if (!result || typeof result !== 'object') return undefined;

    const asMsg = (v: unknown): string | undefined => {
      if (typeof v !== 'string') return undefined;
      const trimmed = v.trim();
      return trimmed.length > 0 ? trimmed : undefined;
    };

    const errorMsg = asMsg(result.error);
    if (errorMsg) return errorMsg;

    if (result.success === false) {
      return asMsg(result.errorMessage) || asMsg(result.error_message) || 'Tool reported failure';
    }

    if (typeof result.status === 'string' && result.status.toLowerCase() === 'error') {
      return asMsg(result.errorMessage) || asMsg(result.error_message) || 'Tool returned status=error';
    }

    return undefined;
  }

  /**
   * Issue #867: Extract gRPC status code from a tool call result. RestApiTool
   * returns {"error": "...", "details": "<ESPv2 JSON>"} where the ESPv2 body
   * contains {"code": 5, "message": "..."}.
   * @returns the gRPC integer code, or -1 if none found
   */
  private extractGrpcCode(result: any): number {
    if (!result || typeof result !== 'object') return -1;
    if (typeof result.code === 'number') return result.code;
    if (typeof result.details === 'string') {
      try {
        const details = JSON.parse(result.details);
        if (typeof details?.code === 'number') return details.code;
      } catch {
        // details is not JSON
      }
    }
    return -1;
  }

  /**
   * Issue #867: Detect "not found" / empty results from tool calls. Used as a
   * fallback when no gRPC code is available. Mirrors the backend
   * ToolCallAccumulator.isNotFoundResult() conventions.
   */
  private static readonly NOT_FOUND_BOOLEAN_KEYS = ['hasAnalysis', 'has_analysis', 'found'];

  private isNotFoundResult(result: any): boolean {
    if (!result || typeof result !== 'object') return false;

    for (const key of ChatComponent.NOT_FOUND_BOOLEAN_KEYS) {
      if (result[key] === false) return true;
    }

    if (Array.isArray(result.results) && result.results.length === 0) return true;
    return false;
  }

  /**
   * Detect ShowObjectOnPage in voice tool results and trigger navigation,
   * mirroring the text-mode interceptUiTools logic in ChatService.
   */
  private interceptVoiceUiTool(toolName: string, result: any): void {
    if (
      toolName === 'ImageObjectSearchService_ShowObjectOnPage' ||
      toolName === 'show_object_on_page' ||
      toolName.includes('ShowObjectOnPage')
    ) {
      const url = result?.previewUrl || result?.preview_url;
      this.navigateIfSafe('ShowObjectOnPage', url);
    }

    // Issue #909: ShowPage — navigate to a page/tab without highlight
    if (toolName === 'UiNavigationService_ShowPage' || toolName === 'show_page' || toolName.includes('ShowPage')) {
      const url = result?.navigationUrl || result?.navigation_url;
      this.navigateIfSafe('ShowPage', url);
    }

    if (
      toolName === 'UiNavigationService_ShowTextOnPage' ||
      toolName === 'show_text_on_page' ||
      toolName.includes('ShowTextOnPage')
    ) {
      const url = result?.navigationUrl || result?.navigation_url;
      this.navigateIfSafe('ShowTextOnPage', url);
    }
  }

  /** Validates a URL from a tool result and navigates if safe. */
  private navigateIfSafe(toolLabel: string, url: unknown): void {
    if (typeof url === 'string' && this.isSafeInternalUrl(url)) {
      ClientLoggingService.log('INFO', `[Chat] Voice tool ${toolLabel}, navigating:`, url);
      this.router.navigateByUrl(url);
    } else if (url) {
      ClientLoggingService.log('WARN', `[Chat] Voice tool ${toolLabel}: rejected unsafe URL`);
    }
  }

  /** Rejects external, protocol-relative, or script URLs; accepts only relative paths. */
  private isSafeInternalUrl(url: string): boolean {
    const s = String(url).trim();
    return s.startsWith('/') && !s.startsWith('//') && !s.includes('://') && !s.toLowerCase().startsWith('javascript:');
  }

  /**
   * Handle decorated transcript from async voice decoration (Issue #941).
   *
   * Matching strategy (Issue #1209):
   *  1. Exact match: bubble content === rawContent
   *  2. Substring match: bubble content contains rawContent (happens when
   *     multiple turns accumulate in one bubble across tool calls). In this
   *     case only the matching substring is replaced with the decorated text.
   *  3. Fallback: most recent assistant voice message (full replacement).
   */
  onVoiceTranscriptDecorated(event: VoiceTranscriptDecoratedEvent): void {
    const messages = this.chatState.currentState.messages;
    const voiceAssistantMessages = messages.filter(
      m => m.role === 'ASSISTANT' && m.modality === ChatComponent.MODALITY_VOICE
    );

    if (voiceAssistantMessages.length === 0) {
      return;
    }

    const exactMatch = voiceAssistantMessages.find(m => m.content === event.rawContent);
    if (exactMatch) {
      this.chatState.updateLastMessage(event.content, exactMatch.messageId);
      ClientLoggingService.log('INFO', `[Chat] Voice transcript decorated (exact): ${exactMatch.messageId}`);
      return;
    }

    const substringMatch = voiceAssistantMessages.find(m => m.content?.includes(event.rawContent));
    if (substringMatch) {
      const updated = substringMatch.content!.replace(event.rawContent, event.content);
      this.chatState.updateLastMessage(updated, substringMatch.messageId);
      ClientLoggingService.log('INFO', `[Chat] Voice transcript decorated (substring): ${substringMatch.messageId}`);
      return;
    }

    const fallback = voiceAssistantMessages[voiceAssistantMessages.length - 1];
    this.chatState.updateLastMessage(event.content, fallback.messageId);
    ClientLoggingService.log('WARN', `[Chat] Voice transcript decorated (fallback): ${fallback.messageId}`);
  }

  /**
   * Handle corrected user transcript from async voice transcript cleanup (Issue #880).
   * Matches by rawContent against voice user messages, falling back to the most recent one.
   */
  onVoiceTranscriptCorrected(event: VoiceTranscriptCorrectedEvent): void {
    const messages = this.chatState.currentState.messages;
    const voiceUserMessages = messages.filter(m => m.role === 'USER' && m.modality === ChatComponent.MODALITY_VOICE);

    if (voiceUserMessages.length === 0) {
      ClientLoggingService.log('WARN', `[Chat] TRANSCRIPT_CORRECTED received but no voice user messages in state`);
      return;
    }

    const exactMatch = voiceUserMessages.find(m => m.content === event.rawContent);
    const target = exactMatch ?? voiceUserMessages[voiceUserMessages.length - 1];
    const matchStrategy = exactMatch ? 'exact' : 'fallback_most_recent';

    this.chatState.updateLastMessage(event.content, target.messageId);
    ClientLoggingService.log(
      'INFO',
      `[Chat] TRANSCRIPT_CORRECTED applied:` +
        ` messageId=${target.messageId},` +
        ` match=${matchStrategy},` +
        ` rawLen=${event.rawContent?.length ?? 0},` +
        ` correctedLen=${event.content?.length ?? 0}`
    );
  }

  onVoiceThinking(text: string): void {
    const msgId = this.getOrCreateVoiceAssistantMessage();
    const target = this.chatState.currentState.messages.find(m => m.messageId === msgId);
    const timeline = target?.metadata?.timeline ? [...target.metadata.timeline] : [];

    const lastEvent = timeline[timeline.length - 1];
    if (lastEvent && lastEvent.type === 'thinking' && !lastEvent.durationMs) {
      timeline[timeline.length - 1] = {
        ...lastEvent,
        content: (lastEvent.content || '') + text,
      };
    } else {
      timeline.push({
        type: 'thinking',
        content: text,
        timestamp: Date.now(),
      });
    }

    this.chatState.updateLastMessageMetadata({ ...(target?.metadata || {}), timeline } as any, msgId);
    this.scrollToBottom();
  }

  /**
   * Remove context item
   */
  removeContext(type: 'project' | 'file' | 'page') {
    const updates: Partial<ChatContext> = {};

    switch (type) {
      case 'project':
        updates.projectId = undefined;
        updates.projectName = undefined;
        break;
      case 'file':
        updates.fileId = undefined;
        updates.fileName = undefined;
        break;
      case 'page':
        updates.pageNumber = undefined;
        break;
    }

    this.chatState.updateContext(updates);
  }

  /**
   * Unpin a page
   */
  unpinPage(page: PinnedPage) {
    this.chatState.removePinnedPage(page);
  }

  /**
   * Open context selection dialog
   */
  openContextDialog() {
    // TODO: Implement dialog for adding context
    ClientLoggingService.log('INFO', 'Open context dialog');
  }

  /**
   * Clear chat history
   */
  clearChat() {
    if (confirm('Are you sure you want to clear this conversation?')) {
      this.createNewSession();
    }
  }

  close() {
    this.chatState.closeChat();
  }

  toggleDocked() {
    this.chatState.toggleDocked();
  }

  /**
   * Start new chat immediately (Issue #371: no confirmation, show snackbar instead)
   */
  confirmNewChat() {
    this.createNewSession();
  }

  /**
   * Open chat history dialog
   */
  openHistory() {
    // Menu is handled by template trigger
  }

  /**
   * Refresh session list when history menu opens (Issue #371)
   */
  onHistoryMenuOpened() {
    if (this.historyList) {
      this.historyList.loadSessions();
    }
  }

  /**
   * Handle session selection from history menu
   */
  onSessionSelected(sessionId: string) {
    if (sessionId) {
      if (!this.chatState.currentState.currentSession && !this.viewingPersistedSession && this.chatInput) {
        this.localDraftMessage = this.chatInput.message;
      }
      this.viewingPersistedSession = true;
      if (this.chatInput) this.chatInput.message = '';
      ClientLoggingService.log('INFO', '[Chat] Switching to session:', sessionId);
      // Close the menu immediately
      if (this.historyTrigger) {
        this.historyTrigger.closeMenu();
      }

      // Pulse the history button first (reverse of minimize: pulse → expand)
      // Cancel any previous pulse from rapid re-selection
      if (this.historyPulseTimerId) clearTimeout(this.historyPulseTimerId);
      this.isHistoryHighlighted = true;
      this.historyPulseTimerId = setTimeout(() => {
        this.isHistoryHighlighted = false;
        this.historyPulseTimerId = null;
      }, ChatComponent.HISTORY_PULSE_MS);

      // Signal that loadSession should play the maximize animation
      this.pendingMaximize = true;
      this.loadSession(sessionId);
    }
  }

  /**
   * Handle session deletion from history menu
   */
  onSessionDeleted(sessionId: string) {
    ClientLoggingService.log('INFO', '[Chat] Session deleted:', sessionId);
    const currentSession = this.chatState.currentState.currentSession;

    // If deleted session is the active one, clear to draft mode
    if (currentSession && currentSession.sessionId === sessionId) {
      ClientLoggingService.log('INFO', '[Chat] Active session deleted, resetting to draft mode');
      this.chatState.resetSessionData();
      if (this.chatInput) this.chatInput.message = this.localDraftMessage ?? '';
      this.snackBar.open('Session deleted', 'Dismiss', {
        duration: 3000,
      });
    }
  }

  // --- Issue #740: Docked layout management ---

  private updateDockedLayout(): void {
    const { isOpen, isDocked } = this.chatState.currentState;
    const width = isOpen && isDocked ? this.effectiveChatWidth : 0;
    this.doc.documentElement.style.setProperty('--chat-docked-width', `${width}px`);
  }

  private clearDockedLayout(): void {
    this.doc.documentElement.style.setProperty('--chat-docked-width', '0px');
  }

  // --- Issue #733: Idle timeout tracking ---

  private static readonly ACTIVITY_EVENTS: string[] = [
    'mousemove',
    'keydown',
    'click',
    'touchstart',
    'scroll',
    'wheel',
  ];

  private readonly onUserActivity = (): void => {
    this.chatService.notifyActivity();
  };

  private readonly onVisibilityChange = (): void => {
    this.chatService.notifyVisibilityChange(this.doc.hidden);
  };

  private setupIdleTracking(): void {
    const opts: AddEventListenerOptions = { passive: true };
    for (const event of ChatComponent.ACTIVITY_EVENTS) {
      this.doc.addEventListener(event, this.onUserActivity, opts);
    }
    this.doc.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  private teardownIdleTracking(): void {
    for (const event of ChatComponent.ACTIVITY_EVENTS) {
      this.doc.removeEventListener(event, this.onUserActivity);
    }
    this.doc.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  /**
   * Copy entire chat log to clipboard (for debugging)
   *
   * Exports the complete chat trajectory including:
   * - All messages (user + assistant)
   * - Timeline events (thinking, tool calls)
   * - Tool call parameters, results, and errors
   * - Session metadata
   * - Context information
   */
  copyChatLog() {
    const state = this.chatState.currentState;

    // Build comprehensive debug log
    const chatLog = {
      session: state.currentSession,
      context: state.context,
      messageCount: state.messages.length,
      queuedCount: state.messageQueue.length,
      messages: state.messages.map(msg => ({
        messageId: msg.messageId,
        role: msg.role,
        content: msg.content,
        timestamp: msg.timestamp,
        queued: msg.queued,
        decorating: msg.decorating,
        metadata: this.mapChatMessageMetadata(msg.metadata),
      })),
      queue: state.messageQueue,
      timestamp: new Date().toISOString(),
    };

    // Copy as formatted JSON
    const jsonString = JSON.stringify(chatLog, null, 2);
    navigator.clipboard
      .writeText(jsonString)
      .then(() => {
        ClientLoggingService.log('INFO', '[Chat] Chat log copied to clipboard');
        ClientLoggingService.log('INFO', 'Log size:', jsonString.length, 'bytes');
      })
      .catch(err => {
        ClientLoggingService.log('ERROR', '[Chat] Failed to copy chat log:', err);
      });
  }

  /** Copy the current session ID to clipboard (Issue #1101 debugging request). */
  copySessionId() {
    const sessionId = this.chatState.currentState.currentSession?.sessionId;
    if (sessionId) {
      navigator.clipboard
        .writeText(sessionId)
        .then(() => {
          this.snackBar.open('Session ID copied to clipboard', undefined, { duration: 3000 });
          ClientLoggingService.log('INFO', '[Chat] Session ID copied to clipboard:', sessionId);
        })
        .catch(err => {
          ClientLoggingService.log('ERROR', '[Chat] Failed to copy Session ID:', err);
        });
    }
  }

  /** Open the dedicated print view in a new browser tab for visual inspection. */
  openPrintPreview() {
    const state = this.chatState.currentState;
    const sessionId = state.currentSession?.sessionId;
    const projectName = state.context?.projectName || state.context?.projectId;
    const projectAddress = state.context?.projectAddress;

    if (sessionId) {
      const params = new URLSearchParams();
      if (projectName) params.append('projectName', projectName);
      if (projectAddress) params.append('projectAddress', projectAddress);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      // Navigating natively opens in a new tab without Headless Chrome hashing mechanics
      window.open(`/print-preview/chat-session/${sessionId}${queryString}`, '_blank', 'noopener,noreferrer');
      ClientLoggingService.log('INFO', '[Chat] Opened Print Preview for Session:', sessionId);
    }
  }

  /** Export current chat session as a branded PDF (Issue #1074). */
  downloadAsPdf() {
    const sessionId = this.chatState.currentState.currentSession?.sessionId;
    if (!sessionId) return;

    this.isExportingPdf = true;
    this.snackBar.open(
      'PDF Export in progress... This may take up to 10 seconds due to server cold-starts.',
      undefined,
      {
        duration: 10000,
      }
    );

    const projectName =
      this.chatState.currentState.context?.projectName || this.chatState.currentState.context?.projectId;
    const projectAddress = this.chatState.currentState.context?.projectAddress;
    this.chatService
      .exportSessionPdf(sessionId, projectName, projectAddress)
      .pipe(
        finalize(() => {
          this.isExportingPdf = false;
        })
      )
      .subscribe({
        next: () => {
          this.snackBar.dismiss();
          ClientLoggingService.log('INFO', '[Chat] PDF downloaded successfully');
        },
        error: (err: unknown) => {
          ClientLoggingService.log('ERROR', '[Chat] PDF export failed:', err);
          this.snackBar.open('PDF Export failed. Please try again.', 'Close', { duration: 5000 });
        },
      });
  }

  /**
   * Open feedback dialog for reporting issues (Issue #577).
   * Pre-populates with current chat log and context.
   * Dialog closes immediately on submit; backend call runs in the background
   * with snackbar notifications (Issue #950).
   */
  openFeedbackDialog(errorMessageId?: string, displayedError?: string) {
    const state = this.chatState.currentState;

    let chatLogJson: string | undefined;
    if (state.messages && state.messages.length > 0) {
      const chatLog = {
        session: state.currentSession,
        context: state.context,
        messages: state.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
          metadata: this.mapChatMessageMetadata(msg.metadata),
          errorCode: msg.errorCode,
          errorCorrelationId: msg.errorCorrelationId,
          errorAction: msg.errorAction,
          retryable: msg.retryable,
        })),
      };
      chatLogJson = JSON.stringify(chatLog, null, 2);
    }

    const safeUiState = {
      context: {
        projectId: state.context?.projectId,
        pageNumber: state.context?.pageNumber,
      },
      sessionId: state.currentSession?.sessionId,
      messageCount: state.messages?.length ?? 0,
      isLoading: state.isLoading,
    };

    const failedMessage = errorMessageId
      ? state.messages.find(message => message.messageId === errorMessageId && message.failed)
      : undefined;
    if (failedMessage || displayedError) {
      this.feedbackReporter.openErrorReport({
        summary: displayedError || failedMessage?.content || 'A chat response failed.',
        errorCode: failedMessage?.errorCode,
        correlationId: failedMessage?.errorCorrelationId,
        operation: failedMessage ? 'chat-response' : 'chat-state',
        occurredAt: failedMessage?.timestamp,
        projectId: state.context?.projectId,
        chatSessionId: state.currentSession?.sessionId,
        chatLogJson,
        uiState: safeUiState,
      });
      return;
    }

    const uiStateJson = JSON.stringify(safeUiState);

    const dialogData: FeedbackDialogData = {
      chatLogJson,
      chatSessionId: state.currentSession?.sessionId,
      pageUrl: this.safeFeedbackPageUrl(),
      uiStateJson,
    };

    const dialogRef = this.dialog.open(FeedbackDialogComponent, {
      data: dialogData,
      width: '560px',
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe((result: FeedbackDialogResult | undefined) => {
      if (!result) return;
      this.submitFeedbackInBackground(result, dialogData);
    });
  }

  openGlobalErrorReport(): void {
    const displayedError = this.chatState.currentState.error;
    if (displayedError) {
      this.openFeedbackDialog(undefined, displayedError);
    }
  }

  private safeFeedbackPageUrl(): string {
    try {
      const pageUrl = new URL(window.location.href);
      return `${pageUrl.origin}${pageUrl.pathname}`;
    } catch {
      return window.location.pathname;
    }
  }

  private feedbackBeforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

  /**
   * Submits feedback in the background after the dialog has closed.
   * Shows snackbar progress/success/error notifications (Issue #950).
   *
   * A `beforeunload` guard is active while the request is in-flight so the
   * browser warns the user if they try to close the tab before the HTTP
   * request has been handed off to Cloud Run.
   */
  private submitFeedbackInBackground(result: FeedbackDialogResult, dialogData: FeedbackDialogData) {
    this.snackBar.open('Submitting feedback...', undefined, { duration: 0 });
    this.addFeedbackBeforeUnloadGuard();

    this.authService.loading$
      .pipe(
        filter(loading => !loading),
        take(1),
        switchMap(() => this.authService.user$.pipe(take(1)))
      )
      .subscribe(user => {
        const request: SubmitFeedbackRequest = {
          description: result.description,
          category: result.category,
          chatSessionId: dialogData.chatSessionId,
          chatLogJson: result.includeChatLog ? dialogData.chatLogJson : undefined,
          pageUrl: dialogData.pageUrl || window.location.href,
          uiStateJson: dialogData.uiStateJson,
          userEmail: result.userConsent ? user?.email || 'authenticated-user' : undefined,
        };

        this.feedbackService.submitFeedback(request).subscribe({
          next: (response: SubmitFeedbackResponse) => {
            this.removeFeedbackBeforeUnloadGuard();
            this.snackBar
              .open(`Feedback submitted! Issue #${response.issueNumber} created.`, 'View Issue', {
                duration: 8000,
                horizontalPosition: 'center',
                verticalPosition: 'bottom',
              })
              .onAction()
              .subscribe(() => {
                if (response.issueUrl?.startsWith('https://')) {
                  window.open(response.issueUrl, '_blank');
                }
              });
          },
          error: (err: { status: number; message: string; raw: unknown }) => {
            this.removeFeedbackBeforeUnloadGuard();
            ClientLoggingService.log(
              'ERROR',
              `[ChatComponent] Feedback submit failed (HTTP ${err.status}):`,
              err.message,
              err.raw
            );
            this.snackBar.open(err.message || 'Failed to submit feedback', 'Dismiss', {
              duration: 10000,
            });
          },
        });
      });
  }

  private addFeedbackBeforeUnloadGuard() {
    this.feedbackBeforeUnloadHandler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', this.feedbackBeforeUnloadHandler);
  }

  private removeFeedbackBeforeUnloadGuard() {
    if (this.feedbackBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.feedbackBeforeUnloadHandler);
      this.feedbackBeforeUnloadHandler = null;
    }
  }

  /** Helper to map chat message metadata for serialization. */
  private mapChatMessageMetadata(metadata: any) {
    if (!metadata) {
      return undefined;
    }
    return {
      thinkingContent: metadata.thinkingContent,
      toolCalls: metadata.toolCalls?.map((tc: ToolCall & { parametersJson?: string; resultJson?: string }) => ({
        ...tc,
        parametersJson: tc.parameters !== undefined ? JSON.stringify(tc.parameters) : tc.parametersJson,
        resultJson: tc.result !== undefined ? JSON.stringify(tc.result) : tc.resultJson,
      })),
      timeline: metadata.timeline?.map((event: TimelineEvent) => {
        if (event.type === 'tool_call' && event.toolCall) {
          const tc = event.toolCall as ToolCall & { parametersJson?: string; resultJson?: string };
          return {
            ...event,
            toolCall: {
              ...tc,
              parametersJson: tc.parameters !== undefined ? JSON.stringify(tc.parameters) : tc.parametersJson,
              resultJson: tc.result !== undefined ? JSON.stringify(tc.result) : tc.resultJson,
            },
          };
        }
        return event;
      }),
      latencyMs: metadata.latencyMs,
      costAnalysis: metadata.costAnalysis,
    };
  }
}
